import React from 'react';
import styles from './OrderPrintout.scss';
import { object, func } from 'prop-types';
import _ from 'lodash';
import { translate } from 'react-i18next';

import { Layout, Cell, Text, TextButton } from 'wix-style-react';

function Divider() {
  return (
    <Cell span={12}>
      <div className={styles.divider} />
    </Cell>
  );
}

function Title({ displayableOrder }) {
  return (
    <React.Fragment>
      <Cell span={6}>
        <Text size="small" weight="normal" secondary data-hook="order-details-date">
          {new Date(displayableOrder.created).toDateString()}
        </Text>
      </Cell>
      <Cell span={6}>
        <Text className={styles.right} size="small" weight="normal" secondary data-hook="order-details-order-id">
          #{displayableOrder.id}
        </Text>
      </Cell>
      <Divider />
    </React.Fragment>
  );
}

export function OrderItem(props) {
  // todo masha move elsewhere?
  const { count, title, price, variations, comments, index } = props;

  return (
    <React.Fragment key={`cart-item-${index}`}>
      <Cell span={1}>
        <Text size="medium" weight="normal" data-hook={`order-details-summary-item-amount-${index}`}>
          {count} x
        </Text>
      </Cell>
      <Cell span={9}>
        <Text tsize="medium" weight="normal" data-hook={`order-details-summary-item-title-${index}`}>
          {title}
        </Text>
      </Cell>
      <Cell span={2}>
        <Text
          size="medium"
          weight="normal"
          className={styles.right}
          data-hook={`order-details-summary-item-price-${index}`}
        >
          {price}
        </Text>
      </Cell>
      {variations && (
        <React.Fragment>
          <Cell span={1} />
          <Cell span={9}>
            <Text
              className={styles.variations}
              size="small"
              weight="normal"
              secondary
              data-hook={`order-details-summary-item-variations-${index}`}
            >
              {variations}
            </Text>
          </Cell>
          <Cell span={2} />
        </React.Fragment>
      )}
      {comments && (
        <React.Fragment>
          <Cell span={1} />
          <Cell span={9}>
            <Text size="small" weight="normal" data-hook={`order-details-summary-item-comments-${index}`} secondary>
              {comments}
            </Text>
          </Cell>
          <Cell span={2} />
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

function Items({ items }) {
  const orderItems = _.map(items, (item, index) => {
    const { count, variations, title, comments, price } = item;
    return (
      <OrderItem
        key={index}
        index={index}
        count={count}
        variations={variations.map((v) => v.text).join(', ')}
        title={title}
        comments={comments}
        price={price}
      />
    );
  });

  return <React.Fragment>{orderItems}</React.Fragment>;
}

function Charges({ charges }) {
  const _charges = charges.map((charge, index) => {
    return (
      <React.Fragment key={`charge-${index}`}>
        <Cell span={10}>
          <Text size="medium" weight="normal" data-hook="order-details-charges-item-title">
            {charge.title}
          </Text>
        </Cell>
        <Cell span={2}>
          <Text size="medium" weight="normal" className={styles.right} data-hook="order-details-charges-item-value">
            {charge.amount}
          </Text>
        </Cell>
      </React.Fragment>
    );
  });

  const hasCharges = _charges.length > 0;

  return (
    <React.Fragment>
      {hasCharges && (
        <React.Fragment>
          {_charges}
          <Divider />
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

function OrderComments({ comment, t }) {
  if (!comment) {
    return null;
  }

  return (
    <React.Fragment>
      <Cell span={10}>
        <Text
          className={styles.comments}
          size="small"
          weight="thin"
          light
          secondary
          data-hook="order-details-special-request"
        >
          {t(comment.title)}
        </Text>
      </Cell>
      <Cell span={2} />
      <Cell span={10}>
        <Text size="medium" weight="normal" data-hook="order-details-comment">
          {comment.text}
        </Text>
      </Cell>
      <Cell span={2} />
      <Divider />
    </React.Fragment>
  );
}

function Summary({ summary, t }) {
  const extras = summary.extras.map((extra) => {
    return {
      price: t(extra.price) || extra.price,
      title: t(extra.title),
    };
  });
  return (
    <React.Fragment>
      {extras.map((extra, index) => {
        return (
          <React.Fragment key={index}>
            <Cell span={10}>
              <Text size="medium" weight="normal" data-hook="order-details-totals-subtotal-title">
                {t(extra.title)}
              </Text>
            </Cell>
            <Cell span={2}>
              <Text
                size="medium"
                weight="normal"
                className={styles.right}
                data-hook="order-details-totals-subtotal-value"
              >
                {extra.price}
              </Text>
            </Cell>
          </React.Fragment>
        );
      })}
      <Divider />
    </React.Fragment>
  );
}

function Total({ total, t }) {
  return (
    <React.Fragment>
      <Cell span={10}>
        <Text size="medium" weight="bold" data-hook="order-details-totals-total-title">
          {t(total.title)}
        </Text>
      </Cell>
      <Cell span={2}>
        <Text size="medium" weight="bold" className={styles.right} data-hook="order-details-totals-total-value">
          {total.text}
        </Text>
      </Cell>
    </React.Fragment>
  );
}

function AddToOrderButton({ onAddToOrderClick, t }) {
  return (
    <React.Fragment>
      <Cell span={12}>
        <div className={styles.addToOrderButton}>
          <TextButton onClick={onAddToOrderClick} dataHook="order-printout-add-to-order">
            {t('phoneordering_menu_orderhistory_add_cta')}
          </TextButton>
        </div>
      </Cell>
      <Divider />
    </React.Fragment>
  );
}

function OrderPrintout({ displayableOrder, order, onAddToOrderClick, t }) {
  return (
    <div className={styles.wrapper}>
      <Layout gap="9px 6px">
        <Title displayableOrder={displayableOrder} />
        <Items items={displayableOrder.cart.items} />
        <AddToOrderButton t={t} order={order} onAddToOrderClick={onAddToOrderClick} />
        <Charges charges={displayableOrder.cart.charges} />
        <OrderComments t={t} comment={displayableOrder.cart.comment} />
        <Summary t={t} summary={displayableOrder.cart.summary} />
        <Total t={t} total={displayableOrder.cart.summary.total} />
      </Layout>
    </div>
  );
}

OrderPrintout.displayName = 'OrderPrintout';
OrderPrintout.propTypes = {
  displayableOrder: object,
  onAddToOrderClick: func,
  t: func.isRequired,
};

export default translate()(OrderPrintout);
